import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { color, font, space } from '@creditornot/alchemy-react/tokens';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useToast } from 'common/hooks/useToast';
import { downloadBlob } from 'common/utils/downloadUtils';
// Intentional cross-import, while figuring out the final order of folders
import { RuleBox } from 'features/inventory-replenishment/components/RuleBox/RuleBox';

import { downloadBulkOperationReport } from '../api';
import { BulkOperationList } from '../api-types';
import { BulkOperationEntry } from '../OverridesView/BulkOperationEntry';

const Item = styled(Stack)`
  padding-bottom: ${space.regularLarge};
  border-bottom: 1px solid ${color.border};
  &:last-child {
    border-bottom: none;
  }
`;

const EmptyState = styled(Stack)`
  padding: ${space.broadXxlarge} ${space.regularXxxlarge};
`;

type Props = {
  open: boolean;
  onDismiss: () => void;
  overrides: BulkOperationList['items'];
};

export const OverridesHistoryModal = ({ open, onDismiss, overrides }: Props) => {
  const format = useFormatMessage();
  const { addErrorToast } = useToast();
  const [countryCode] = useSelectedCountryCodes();

  const { mutate: mutateDownloadReport, isPending: downloadReportPending } = useMutation({
    mutationFn: (id: string) => downloadBulkOperationReport(id),
    onSuccess: (blob) => {
      const date = DateTime.now().toISO().slice(0, 10);
      const fileName = `${date}-${countryCode}-bulk-operation-report.csv`;
      downloadBlob(blob, fileName);
    },
    onError: addErrorToast,
  });

  return (
    <RuleBox
      open={open}
      onDismiss={onDismiss}
      title={format('forecasting.override_history')}
      size="xsmall"
    >
      <RuleBox.Body>
        {overrides.length === 0 ? (
          <EmptyState gap="regularLarge" alignItems="center" justifyContent="center">
            <Icon source={icons.faceNeutral} size="medium" fill={color.textSubdued} />
            <div style={{ font: font.caption, color: color.textSubdued }}>
              {format('inventory_replenishment.no_overrides_history')}
            </div>
          </EmptyState>
        ) : (
          <Stack gap="regularLarge">
            {overrides.map((override) => (
              <Item key={override.id}>
                <BulkOperationEntry
                  operation={override}
                  downloadReportPending={downloadReportPending}
                  onDownloadReport={() => mutateDownloadReport(override.id)}
                />
              </Item>
            ))}
          </Stack>
        )}
      </RuleBox.Body>
    </RuleBox>
  );
};
